import { defineStore } from 'pinia';
import type { MP3DPOffer } from '~/models/labs';
import type {
  Machine,
  ProcessType,
  ProcessFamily,
  MaterialClass,
  Material,
  MapMaterials,
  Process,
  MaterialFamily,
  MaterialType,
  Covering,
  Specularity,
} from '~/models/referential';
import $fetch from '~/modules/fetch';

type IReferentialStore = {
  offers: any[];
  colors: any[];
  machines: Machine[];
  processTypes: ProcessType[];
  processes: Process[];
  processFamilies: ProcessFamily[];
  materialClasses: MaterialClass[];
  materialFamilies: MaterialFamily[];
  materials: Material[];
  materialTypes: MaterialType[];
  coverings: Covering[];
  specularities: Specularity[];
};

const pushUnique = (arr: string[], value?: string) => {
  if (value && !arr.includes(value)) {
    arr.push(value);
  }
};

const getMapFromOffers = (offers: MP3DPOffer[]): Record<string, string[]> => {
  const map: Record<string, string[]> = {
    processFamilies: [],
    processTypes: [],
    processes: [],
    materialFamilies: [],
    materialClasses: [],
    materialTypes: [],
    materials: [],
    machines: [],
    printers: [],
    colors: [],
    coverings: [],
  };

  offers.forEach((offer) => {
    pushUnique(map.processFamilies, offer.processFamilyId);
    pushUnique(map.processes, offer.processId);
    pushUnique(map.materialFamilies, offer.materialFamilyId);
    pushUnique(map.materialClasses, offer.materialClassId);
    pushUnique(map.materialTypes, offer.materialTypeId);
    pushUnique(map.materials, offer.materialId);
    pushUnique(map.printers, offer.printerId);
  });

  return map;
};

export const useReferentialStore = defineStore({
  id: 'referential',
  state: (): IReferentialStore => ({
    offers: [],
    colors: [],
    machines: [],
    processTypes: [],
    processes: [],
    processFamilies: [],
    materialFamilies: [],
    materialClasses: [],
    materials: [],
    materialTypes: [],
    coverings: [],
    specularities: [],
  }),
  getters: {
    mapMachines(s) {
      const map: Record<string, Machine> = {};
      s.machines.forEach((machine) => {
        map[machine.id] = machine;
      });
      return map;
    },
    mapMaterialFamillies(s) {
      const map: Record<string, MaterialFamily> = {};

      s.materialFamilies.forEach((m) => {
        map[m.id] = m;
      });

      return map;
    },
    mapMaterials() {
      const map: MapMaterials = {
        family: {},
        class: {},
        type: {},
        name: {},
        materials: {},
        machines: {},
        processes: {},
      };

      // Force manual ordering
      map.family['MF-1e82b7c90d706677b41254ee7539915e'] = undefined;
      map.family['MF-1e82b7c9ce4c6f12bf2354ee7539915e'] = undefined;
      map.family['MF-1e82b7c9e29a6aff99eb54ee7539915e'] = undefined;
      map.family['MF-1e82b7c9e9e665e5af2b54ee7539915e'] = undefined;
      map.family['MF-1e82b7ca6abd6cddb8a254ee7539915e'] = undefined;
      map.family['MF-1e82b7c9d1c569f2bdb554ee7539915e'] = undefined;

      this.materials.forEach((material) => {
        if (material.type.class.family.name === 'Glass') {
          return;
        } // WE DON'T DISPLAY GLASS

        map.name[material.id] = material.name;
        map.type[material.type.id] = material.type.name;
        map.class[material.type.class.id] = material.type.class.name;
        map.family[material.type.class.family.id] = material.type.class.family.name;
        map.materials[material.id] = material;
      });
      map.family = Object.fromEntries(
        Object.entries(map.family as Record<string, string>).filter(([, value]) => value),
      );

      return map;
    },
    mapOffersFilteredForPart(s) {
      return function (
        part: {
          MaterialID: any;
          Process: string | undefined;
          ProcessFamily: string | undefined;
          MaterialType: string;
          MaterialSubCategory: string;
          MaterialCategory: string;
        },
        offers?: MP3DPOffer[],
      ) {
        offers = offers || s.offers;

        if (part.MaterialID) {
          // We have a MaterialID, filter is simple
          return getMapFromOffers(
            offers.filter((offer: { materialId: any }) => offer.materialId === part.MaterialID),
          );
        }

        // First, filter by process or process family
        if (part.Process) {
          offers = offers.filter((offer) => offer.processId === part.Process);
        } else if (part.ProcessFamily) {
          offers = offers.filter((offer) => offer.processFamilyId === part.ProcessFamily);
        }

        // Then, filter by material
        if (part.MaterialType) {
          offers = offers.filter((offer) => offer.materialTypeId === part.MaterialType);
        }

        if (part.MaterialSubCategory) {
          offers = offers.filter((offer) => offer.materialClassId === part.MaterialSubCategory);
        }

        if (part.MaterialCategory) {
          offers = offers.filter((offer) => offer.materialFamilyId === part.MaterialCategory);
        }

        return getMapFromOffers(offers);
      };
    },
    mapProcessFamilies() {
      const map: Record<string, ProcessFamily> = {};

      this.processFamilies.forEach((processFamily) => {
        map[processFamily.id] = processFamily;
      });

      return map;
    },
    mapProcessFamiliesByFormat(s) {
      const map: Record<string, any> = {};

      s.processFamilies.forEach((el) => {
        el.fileFormats.forEach((format) => {
          if (!map[format.name]) {
            map[format.name] = [];
          }

          if (!map[format.name].includes(el)) {
            map[format.name].push(el.id);
          }
        });
      });

      return map;
    },
    mapMaterialClasses() {
      const map: Record<string, MaterialClass> = {};

      this.materialClasses.forEach((materialClass) => {
        map[materialClass.id] = materialClass;
      });

      return map;
    },
    mapProcesses() {
      const map: Record<string, Process> = {};

      this.processFamilies.forEach((processFamily) => {
        processFamily.processes.forEach((process: Process) => {
          map[process.id] = process;
        });
      });

      return map;
    },
  },
  actions: {
    async fetchOffers() {
      const offers = (await $fetch.get('/resources/mdshop/public/shops/processes', {
        cache: true,
      })) as MP3DPOffer[];

      this.offers = offers;

      return offers;
    },
    setMachines(machines: any) {
      this.machines = machines;
    },
    async fetchMachines() {
      this.machines = (await $fetch.get('/api/v1/machines', {
        cache: true,
        service: 'makedatamgt',
      })) as Machine[];
    },
    /* MACHINE */
    async fetchMachine(payload: { id: string }) {
      const machine = (await $fetch.get(`/api/v1/machines/${payload.id}`, {
        service: 'makedatamgt',
      })) as Machine;
      this.machines.push(machine);
      return machine;
    },
    async addMachine(payload: any) {
      const machine = (await $fetch.post('/api/v1/machines/', {
        data: payload,
        service: 'makedatamgt',
      })) as Machine;

      this.machines.push(machine);

      return machine;
    },
    async editMachine(payload: any) {
      const machine = (await $fetch.put(`/api/v1/machines/${payload.id}`, {
        service: 'makedatamgt',
        data: {
          model: payload.model,
          manufacturer: payload.manufacturer,
          process: payload.process,
          accuracyAbsoluteX: payload.accuracyAbsoluteX,
          accuracyAbsoluteY: payload.accuracyAbsoluteY,
          accuracyAbsoluteZ: payload.accuracyAbsoluteZ,
          maxResolutionX: payload.maxResolutionX,
          maxResolutionY: payload.maxResolutionY,
          maxResolutionZ: payload.maxResolutionZ,
          maxVolumeBoundaryX: payload.maxVolumeBoundaryX,
          maxVolumeBoundaryY: payload.maxVolumeBoundaryY,
          maxVolumeBoundaryZ: payload.maxVolumeBoundaryZ,
          minVolumeBoundaryX: payload.minVolumeBoundaryX,
          minVolumeBoundaryY: payload.minVolumeBoundaryY,
          minVolumeBoundaryZ: payload.minVolumeBoundaryZ,
          image: payload.image,
        },
      })) as Machine;

      const indexToEdit = this.machines.findIndex((el: any) => el.id === payload.id);
      this.machines[indexToEdit] = machine;
      this.machines.splice(indexToEdit, 1, machine);
    },
    async deleteMachine(payload: { id: string }) {
      await $fetch.remove(`/api/v1/machines/${payload.id}`, {
        service: 'makedatamgt',
      });

      const indexToDelete = this.machines.findIndex((el: any) => el.id === payload.id);
      this.machines.splice(indexToDelete, 1);
    },
    // SPECULARITIES
    async fetchSpecularities(payload: any) {
      const specularities = (await $fetch.get('/api/v1/specularities/', {
        cache: true,
        service: 'makedatamgt',
        force: payload?.force,
      })) as Specularity[];

      this.specularities = specularities;
    },
    // PRINTERS
    async fetchMachinesByParams(payload: { process?: string; search?: string }) {
      const printers = (await $fetch.get('/api/v1/machines/', {
        cache: true,
        service: 'makedatamgt',
        query: payload,
      })) as Machine[];

      return printers;
    },
    // PROCESS FAMLIES
    async fetchProcessFamilies(payload = { finishing: false }) {
      const result = (await $fetch.get('/api/v1/process-families', {
        cache: true,
        service: 'makedatamgt',
      })) as ProcessFamily[];

      this.processFamilies = payload.finishing
        ? result
        : result.filter((pf) => pf.name !== 'Finishing');
    },
    async addProcessFamily(payload: any) {
      const processFamily = (await $fetch.post('/api/v1/process-families', {
        data: payload,
        service: 'makedatamgt',
      })) as ProcessFamily;

      this.processFamilies.push(processFamily);

      return processFamily;
    },
    async editProcessFamily(payload: any) {
      const processFamily = (await $fetch.put(`/api/v1/process-families/${payload.id}`, {
        data: {
          name: payload.name,
        },
        service: 'makedatamgt',
      })) as ProcessFamily;

      const indexToEdit = this.processFamilies.findIndex((el) => el.id === payload.id);
      this.processFamilies[indexToEdit] = processFamily;
      this.processFamilies.splice(indexToEdit, 1, processFamily);

      return processFamily;
    },
    async deleteProcessFamily(payload: { id: string }) {
      (await $fetch.remove(`/api/v1/process-families/${payload.id}`, {
        service: 'makedatamgt',
      })) as ProcessFamily;

      const indexToDelete = this.processFamilies.findIndex((el) => el.id === payload.id);
      this.processFamilies.splice(indexToDelete, 1);
    },
    // PROCESS TYPES
    async fetchProcessTypes(payload = { finishing: false }) {
      const result = (await $fetch.get('/api/v1/process-types', {
        cache: true,
        service: 'makedatamgt',
      })) as ProcessType[];

      this.processTypes = payload.finishing
        ? result
        : result.filter((pf) => pf.name !== 'Finishing');
    },
    async addProcessType(payload: any) {
      const processType = (await $fetch.post('/api/v1/process-types', {
        data: payload,
        service: 'makedatamgt',
      })) as ProcessType;

      this.processTypes.push(processType);

      return processType;
    },
    async editProcessType(payload: any) {
      const processType = (await $fetch.put(`/api/v1/process-types/${payload.id}`, {
        data: {
          name: payload.name,
        },
        service: 'makedatamgt',
      })) as ProcessType;

      const indexToEdit = this.processTypes.findIndex((el) => el.id === payload.id);
      this.processTypes[indexToEdit] = processType;
      this.processTypes.splice(indexToEdit, 1, processType);

      return processType;
    },
    async deleteProcessType(payload: { id: string }) {
      (await $fetch.remove(`/api/v1/process-types/${payload.id}`, {
        service: 'makedatamgt',
      })) as ProcessType;

      const indexToDelete = this.processTypes.findIndex((el) => el.id === payload.id);
      this.processTypes.splice(indexToDelete, 1);
    },
    // PROCESSES
    async fetchProcesses(payload = { finishing: false }) {
      const result = (await $fetch.get('/api/v1/processes', {
        cache: true,
        service: 'makedatamgt',
      })) as Process[];

      this.processes = payload.finishing ? result : result.filter((pf) => pf.name !== 'Finishing');
    },
    async addProcess(payload: any) {
      const toSend = payload;

      if (payload.coverings?.length) {
        toSend.category = 'FINISHING';
      } else {
        toSend.category = 'PROCESS';
      }

      const process = (await $fetch.post('/api/v1/processes', {
        data: toSend,
        service: 'makedatamgt',
      })) as Process;

      this.processes.push(process);

      return process;
    },
    async editProcess(payload: any) {
      const toSend = payload;

      if (payload.formatedCoverings?.length) {
        toSend.category = 'FINISHING';
      } else {
        toSend.category = 'PROCESS';
      }

      const process = (await $fetch.put(`/api/v1/processes/${payload.id}`, {
        data: {
          name: payload.name,
          family: { id: payload.familyId },
          type: payload.type,
          coverings: payload.formatedCoverings?.map((el: { value: any }) => ({ id: el.value })),
          category: toSend.category,
        },
        service: 'makedatamgt',
      })) as Process;

      const indexToEdit = this.processes.findIndex((el) => el.id === payload.id);
      this.processes[indexToEdit] = process;
      this.processes.splice(indexToEdit, 1, process);

      return process;
    },
    async deleteProcess(payload: { id: string }) {
      (await $fetch.remove(`/api/v1/processes/${payload.id}`, {
        service: 'makedatamgt',
      })) as Process;

      const indexToDelete = this.processes.findIndex((el) => el.id === payload.id);
      this.processes.splice(indexToDelete, 1);
    },
    // MATERIAL TYPES
    async fetchMaterialTypes() {
      const result = (await $fetch.get('/api/v1/material-types', {
        cache: true,
        service: 'makedatamgt',
      })) as MaterialType[];

      this.materialTypes = result;
    },
    async fetchMaterialType(id: string) {
      const materialType = (await $fetch.get(`/api/v1/material-types/${id}`, {
        cache: true,
        service: 'makedatamgt',
      })) as MaterialType;

      return materialType;
    },
    async addMaterialType(payload: any) {
      const materialType = (await $fetch.post('/api/v1/material-types', {
        data: payload,
        service: 'makedatamgt',
      })) as MaterialType;

      this.materialTypes.push(materialType);

      return materialType;
    },
    async editMaterialType(payload: any) {
      const materialType = (await $fetch.put(`/api/v1/material-types/${payload.id}`, {
        data: {
          name: payload.name,
          materialClass: { id: payload.classId },
        },
        service: 'makedatamgt',
      })) as MaterialType;

      const indexToEdit = this.materialTypes.findIndex((el) => el.id === payload.id);
      this.materialTypes[indexToEdit] = materialType;
      this.materialTypes.splice(indexToEdit, 1, materialType);

      return materialType;
    },
    async deleteMaterialType(payload: { id: string }) {
      (await $fetch.remove(`/api/v1/material-types/${payload.id}`, {
        service: 'makedatamgt',
      })) as MaterialType;

      const indexToDelete = this.materialTypes.findIndex((el) => el.id === payload.id);
      this.materialTypes.splice(indexToDelete, 1);
    },
    // COVERINGS
    async fetchCoverings() {
      const result = (await $fetch.get('/api/v1/coverings', {
        cache: true,
        service: 'makedatamgt',
      })) as Covering[];

      this.coverings = result;
    },
    async addCovering(payload: any) {
      const covering = (await $fetch.post('/api/v1/coverings', {
        data: payload,
        service: 'makedatamgt',
      })) as Covering;

      this.coverings.push(covering);

      return covering;
    },
    async editCovering(payload: any) {
      const covering = (await $fetch.put(`/api/v1/coverings/${payload.id}`, {
        data: {
          name: payload.name,
        },
        service: 'makedatamgt',
      })) as Covering;

      const indexToEdit = this.coverings.findIndex((el) => el.id === payload.id);
      this.coverings[indexToEdit] = covering;
      this.coverings.splice(indexToEdit, 1, covering);

      return covering;
    },
    async deleteCovering(payload: { id: string }) {
      (await $fetch.remove(`/api/v1/coverings/${payload.id}`, {
        service: 'makedatamgt',
      })) as Covering;

      const indexToDelete = this.coverings.findIndex((el) => el.id === payload.id);
      this.coverings.splice(indexToDelete, 1);
    },
    // MATERIAL CLASSES
    async fetchMaterialClasses() {
      const result = (await $fetch.get('/api/v1/material-classes', {
        cache: true,
        service: 'makedatamgt',
      })) as MaterialClass[];

      this.materialClasses = result;
    },
    async addMaterialClass(payload: any) {
      const materialClass = (await $fetch.post('/api/v1/material-classes', {
        data: payload,
        service: 'makedatamgt',
      })) as MaterialClass;

      this.materialClasses.push(materialClass);

      return materialClass;
    },
    async editMaterialClass(payload: any) {
      const materialClass = (await $fetch.put(`/api/v1/material-classes/${payload.id}`, {
        data: {
          name: payload.name,
          family: { id: payload.familyId },
        },
        service: 'makedatamgt',
      })) as MaterialClass;

      const indexToEdit = this.materialClasses.findIndex((el) => el.id === payload.id);
      this.materialClasses[indexToEdit] = materialClass;
      this.materialClasses.splice(indexToEdit, 1, materialClass);

      return materialClass;
    },
    async deleteMaterialClass(payload: { id: string }) {
      (await $fetch.remove(`/api/v1/material-classes/${payload.id}`, {
        service: 'makedatamgt',
      })) as MaterialClass;

      const indexToDelete = this.materialClasses.findIndex((el) => el.id === payload.id);
      this.materialClasses.splice(indexToDelete, 1);
    },
    // MATERIAL FAMILIES
    async fetchMaterialFamilies() {
      const result = (await $fetch.get('/api/v1/material-families', {
        cache: true,
        service: 'makedatamgt',
      })) as MaterialFamily[];

      this.materialFamilies = result;
    },
    async addMaterialFamily(payload: any) {
      const materialFamily = (await $fetch.post('/api/v1/material-families', {
        data: payload,
        service: 'makedatamgt',
      })) as MaterialFamily;

      this.materialFamilies.push(materialFamily);

      return materialFamily;
    },
    async editMaterialFamily(payload: {
      id: string;
      name: string;
      createdAt: Date;
      updatedAt: Date;
    }) {
      const materialFamily = (await $fetch.put(`/api/v1/material-families/${payload.id}`, {
        data: {
          name: payload.name,
        },
        service: 'makedatamgt',
      })) as MaterialFamily;

      const indexToEdit = this.materialFamilies.findIndex((el) => el.id === payload.id);
      this.materialFamilies[indexToEdit] = materialFamily;
      this.materialFamilies.splice(indexToEdit, 1, materialFamily);

      return materialFamily;
    },
    async deleteMaterialFamily(payload: { id: string }) {
      (await $fetch.remove(`/api/v1/material-families/${payload.id}`, {
        service: 'makedatamgt',
      })) as MaterialFamily;

      const indexToDelete = this.materialFamilies.findIndex((el) => el.id === payload.id);
      this.materialFamilies.splice(indexToDelete, 1);
    },
    async fetchMaterialsByIds(payload = { materialIds: [] }) {
      const tabs = [];
      const maxParametersPerCall = 100; // HTTP GETS require MAX 2,048Char
      for (let index = 0; index < payload.materialIds.length; index += maxParametersPerCall) {
        tabs.push(payload.materialIds.slice(index, index + maxParametersPerCall));
      }

      const materials = (
        await Promise.all(
          tabs.map(
            async (materialIds) =>
              $fetch.get('/api/v1/materials', {
                cache: true,
                service: 'makedatamgt',
                query: {
                  ids: materialIds
                    .map((id: any) => `ids=${id}&`)
                    .join('')
                    .slice(4, -1),
                },
              }) as Promise<Material>,
          ),
        )
      ).flat(); // Concat all arrays into one

      if (payload.add) {
        if (this.materials.length === 0) {
          this.materials = materials;

          return;
        }

        const materialsToAdd = materials.filter(
          (material) => !this.mapMaterials.materials[material.id],
        );

        if (materialsToAdd.length) {
          this.materials = this.materials.concat(materialsToAdd);
        }
      } else {
        this.materials = materials;
      }

      return materials;
    },
    async resetMaterialsCache() {
      await $fetch.post('/api/v1/materials/reset', {
        cache: false,
        service: 'makedatamgt',
      });
    },
    async fetchMaterials(payload: any) {
      const query = {
        process: '',
        'process-family': '',
      };

      if (payload?.processId) {
        query.process = payload.processId;
      } else if (payload?.processFamilyId) {
        query['process-family'] = payload.processFamilyId;
      }

      const result = (await $fetch.get('/api/v1/materials', {
        cache: !payload?.force,
        service: 'makedatamgt',
        query,
      })) as Material[];

      if (payload?.add) {
        if (this.materials.length === 0) {
          this.materials = result;

          return;
        }

        const materialsToAdd = result.filter(
          (material: { id: string }) => !this.mapMaterials.materials[material.id],
        );

        if (materialsToAdd.length) {
          this.materials = this.materials.concat(materialsToAdd);
        }
      } else {
        this.materials = result;
      }

      return result;
    },
    resetMaterials() {
      this.materials = [];
    },
    async addMaterial(payload: any) {
      const material = (await $fetch.post('/api/v1/materials', {
        data: payload,
        service: 'makedatamgt',
      })) as Material;

      this.materials.push(material);

      return material;
    },
    async editMaterial(payload: any) {
      const material = (await $fetch.put(`/api/v1/materials/${payload.id}`, {
        data: {
          name: payload.name,
          manufacturer: payload.manufacturer,
          colors: payload.formatedColors?.map((el: { value: string }) => el.value),
          coverings: payload.formatedCoverings?.map((el: { value: any }) => ({ id: el.value })),
          specularities: payload.formattedSpecularities?.map((el: { value: any }) => ({
            id: el.value,
          })),
          type: payload.type,
          processes: payload.formattedProcesses?.map((el: { value: any }) => ({ id: el.value })),
        },
        service: 'makedatamgt',
      })) as Material;

      const indexToEdit = this.materials.findIndex((el) => el.id === payload.id);
      this.materials[indexToEdit] = material;
      this.materials.splice(indexToEdit, 1, material);

      return material;
    },
    async deleteMaterial(payload: { id: string }) {
      (await $fetch.remove(`/api/v1/materials/${payload.id}`, {
        service: 'makedatamgt',
      })) as Material;

      const indexToDelete = this.materials.findIndex((el) => el.id === payload.id);
      this.materials.splice(indexToDelete, 1);
    },
    // CSV
    async fetchMaterialsCSV() {
      await $fetch.get('/api/v1/materials', {
        cache: false,
        file: true,
        csv: true,
        noThrottle: true,
        service: 'makedatamgt',
      });
    },
    async fetchMachinesCSV() {
      await $fetch.get('/api/v1/machines', {
        cache: false,
        file: true,
        csv: true,
        noThrottle: true,
        service: 'makedatamgt',
      });
    },
    async updateMaterialsByCsv(payload: any) {
      const result = await $fetch.post('/api/v1/materials', {
        form: payload,
        service: 'makedatamgt',
      });

      return result;
    },
    async updateMachinesByCsv(payload: any) {
      const result = await $fetch.post('/api/v1/machines', {
        form: payload,
        service: 'makedatamgt',
      });

      return result;
    },
  },
});
